/**
 * Image upload modal with drag-and-drop support.
 * Uses useFileUpload hook for upload state management when uploadService is provided.
 * Falls back to uploadImage prop for backward compatibility.
 */

import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import { Image, Upload, AlertCircle } from 'lucide-react';
import { ContentModal } from './ContentModal';
import { Button } from '../button/Button';
import { useFileUpload } from '../upload/useFileUpload';
import { FileUploadProgress } from '../upload/FileUploadProgress';
import type { FileUploadService, FileUploadResult } from '@airclou/utils';

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelect: (imageUrl: string) => void;
  zIndex?: number;
  /** @deprecated Use uploadService instead for better state management */
  uploadImage?: (file: File) => Promise<string>;
  /** File upload service instance (preferred over uploadImage) */
  uploadService?: FileUploadService;
  /** Category for uploads when using uploadService. Defaults to 'images' */
  category?: string;
  /** Max file size in MB. Defaults to 10 */
  maxFileSizeMB?: number;
}

// Default allowed image MIME types
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
];

export const ImageUploadModal = forwardRef<HTMLDivElement, ImageUploadModalProps>(
  ({ 
    isOpen, 
    onClose, 
    onImageSelect, 
    zIndex = 99999, 
    uploadImage,
    uploadService,
    category = 'images',
    maxFileSizeMB = 10,
  }, _ref) => {
    const [dragOver, setDragOver] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    
    // Legacy mode state (when using uploadImage prop)
    const [legacyUploading, setLegacyUploading] = useState(false);
    const [legacyError, setLegacyError] = useState('');

    // Use hook when uploadService is provided
    const maxFileSize = maxFileSizeMB * 1024 * 1024;
    
    // Create a stable upload state for hook mode
    const hookState = uploadService ? useFileUpload({
      uploadService,
      category,
      allowedMimeTypes: ALLOWED_IMAGE_TYPES,
      maxFileSize,
      onSuccess: (result: FileUploadResult) => {
        onImageSelect(result.url);
        onClose();
      },
    }) : null;

    const isUsingHook = !!uploadService && !!hookState;
    
    // Combined state for UI
    const isUploading = isUsingHook ? hookState.isUploading : legacyUploading;
    const error = isUsingHook ? hookState.error?.message : legacyError;
    const uploadState = isUsingHook ? hookState.state : null;

    // Reset state when modal closes
    useEffect(() => {
      if (!isOpen) {
        setUrlInput('');
        setLegacyError('');
        hookState?.reset();
      }
    }, [isOpen]);

    const handleFileUpload = useCallback(async (file: File) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        if (isUsingHook) {
          // Hook will handle this via allowedMimeTypes
        } else {
          setLegacyError('Please select an image file');
        }
        return;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        if (!isUsingHook) {
          setLegacyError(`File size must be less than ${maxFileSizeMB}MB`);
        }
        return;
      }

      if (isUsingHook && hookState) {
        // Use hook for upload
        await hookState.upload(file);
      } else if (uploadImage) {
        // Legacy mode
        setLegacyUploading(true);
        setLegacyError('');

        try {
          const imageUrl = await uploadImage(file);
          onImageSelect(imageUrl);
          onClose();
        } catch (err) {
          console.error('Image upload failed:', err);
          setLegacyError(err instanceof Error ? err.message : 'Failed to upload image. Please try again.');
        } finally {
          setLegacyUploading(false);
        }
      } else {
        // Fallback to blob URL (not recommended for production)
        const previewUrl = URL.createObjectURL(file);
        onImageSelect(previewUrl);
        onClose();
      }
    }, [isUsingHook, hookState, uploadImage, maxFileSize, maxFileSizeMB, onImageSelect, onClose]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    }, [handleFileUpload]);

    const handleDrop = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      
      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find(file => file.type.startsWith('image/'));
      
      if (imageFile) {
        handleFileUpload(imageFile);
      }
    }, [handleFileUpload]);

    const handleUrlSubmit = useCallback(() => {
      if (urlInput.trim()) {
        onImageSelect(urlInput.trim());
        onClose();
      }
    }, [urlInput, onImageSelect, onClose]);

    // Force close any open tippy popovers when modal opens
    useEffect(() => {
      if (isOpen) {
        const tippyInstances = document.querySelectorAll('[data-tippy-root]');
        tippyInstances.forEach(instance => {
          const element = instance as any;
          if (element._tippy) {
            element._tippy.hide();
          }
        });
      }
    }, [isOpen]);

    return (
      <ContentModal
        isOpen={isOpen}
        onClose={onClose}
        title="Add Image"
        size="md"
        zIndex={zIndex}
        footer={
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        }
      >
        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
          </div>
        )}

        {/* Upload progress (hook mode only) */}
        {isUsingHook && uploadState && uploadState.status !== 'idle' && (
          <FileUploadProgress
            file={uploadState.status === 'uploading' ? uploadState.file : undefined}
            status={uploadState.status}
            errorMessage={uploadState.status === 'error' ? uploadState.error.message : undefined}
            className="mb-4"
          />
        )}

        {/* File Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver
              ? 'border-primary bg-primary/5 dark:bg-primary/10'
              : 'border-gray-300 dark:border-gray-600'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {isUploading ? 'Uploading...' : 'Drop an image here or click to browse'}
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="image-upload"
            disabled={isUploading}
          />
          <label
            htmlFor="image-upload"
            className={`inline-flex items-center px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-[hsl(var(--primary-hover))] cursor-pointer transition-colors ${
              isUploading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isUploading ? 'Uploading...' : 'Browse Files'}
          </label>
        </div>

        {/* URL Input */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Or enter image URL
          </label>
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="field-text flex-1 px-3 py-1.5 border border-field rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-none focus:shadow-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUrlSubmit();
                }
              }}
            />
            <button
              onClick={handleUrlSubmit}
              className="text-sm px-4 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-[hsl(var(--primary-hover))] transition-colors disabled:opacity-50"
              disabled={!urlInput.trim()}
            >
              Add
            </button>
          </div>
        </div>
      </ContentModal>
    );
  }
);

ImageUploadModal.displayName = 'ImageUploadModal';